import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import PerformanceDocumentTableRow from './PerformanceDocumentTableRow'

import {
    ContractPerformance,
    ContractPerformanceStage,
} from '../../../../types'

import {
    ContractPerformanceDocument,
    PerformanceDocumentsError,
} from '../../types'

import LoadingIcon from '../../../../../../../components/LoadingIcon'

import { CurrencyList } from '../../../types'
import HoverTooltip from '../../../../../../../components/HoverTooltip'

import PerformanceDocumentFileUploaderBlock from './PerformanceDocumentFileUploaderBlock'
import { Slide, ToastContainer } from 'react-toastify'

type PerfTotalDocSum = {
    total_doc: number
    total_doc_rub: number
}

type PerfTotalPaymentSum = {
    total_payment: number
    total_payment_rub: number
}

type Props = {
    theme: string
    perfAction: boolean
    performanceGuid: string | undefined
    role: string
    onPerformanceDocuments: (have_perf_documents: boolean) => void
    errors?: PerformanceDocumentsError
    onAnyEdit: () => void
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const PerformanceDocumentTable: React.FC<Props> = (props) => {
    const navigate = useNavigate()

    const [performanceDocuments, setPerformanceDocuments] = useState<
        ContractPerformanceDocument[] | null
    >(null)
    const [isLoadingPerformanceDocuments, setLoadingPerformanceDocuments] =
        useState(false)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [isAttachment, setAttachment] = useState(true)

    const getPerformanceContractDocuments = async (): Promise<
        ContractPerformanceDocument[]
    > => {
        setLoadingPerformanceDocuments(true)
        return fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_attachments?performance_contract_guid=${props.performanceGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на документы')
                }
            })
            .catch((e) => {
                // console.error(e)
            })
            .finally(() => {
                setLoadingPerformanceDocuments(false)
            })
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (perfDocument_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(perfDocument_guid)) {
                return prevSelectedItems.filter(
                    (itemId) => itemId !== perfDocument_guid
                )
            } else {
                return [...prevSelectedItems, perfDocument_guid]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (perfDocument_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(perfDocument_guid)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, perfDocument_guid] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            // Выбираем только те элементы, которые соответствуют текущей стадии
            const filteredItems = performanceDocuments
                ? performanceDocuments.map((item) => item.guid)
                : []

            setSelectedItems(filteredItems)
        } else {
            // Сбрасываем выбор всех элементов
            setSelectedItems([])
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (props.performanceGuid) {
                    const perfDocuments =
                        await getPerformanceContractDocuments()
                    if (perfDocuments) {
                        setPerformanceDocuments(perfDocuments)
                    }
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    return (
        <fieldset disabled={false}>
            <div className="flex flex-col mt-3 space-y-1 w-full items-center justify-start">
                <PerformanceDocumentFileUploaderBlock
                    theme={props.theme}
                    perfAction={props.perfAction}
                    performanceGuid={String(props.performanceGuid)}
                    isAttachment={isAttachment}
                    role={props.role}
                    onPerformanceDocuments={props.onPerformanceDocuments}
                    errors={props.errors}
                    onAnyEdit={props.onAnyEdit}
                />

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar
                    newestOnTop
                    closeOnClick
                    draggable
                    pauseOnHover
                    theme={props.theme == 'dark' ? 'dark' : 'light'}
                    transition={Slide}
                    className="!z-[9999]"
                    toastClassName={() =>
                        'flex w-fit min-h-[40px] px-3 py-2 items-center gap-2 bg-[#f6f6f6] dark:bg-[#2A282B] rounded-xl shadow-xl text-[#181818] dark:text-[#FFFFFF] text-[13px] my-1'
                    }
                />
            </div>
        </fieldset>
    )
}

export default PerformanceDocumentTable
